/**
 * Segmentation Config Service
 * 
 * Handles fetching and saving segmentation configurations from ISE backend
 * (not JAMS directly)
 */

/**
 * Response type for fetching segmentation config from ISE
 */
export interface ISegmentationConfigResponse {
  success: boolean
  data: {
    jazelId: string
    segments: Record<string, {
      name: string
      exclusive: boolean
      priority: number
      custom?: boolean
      ruleset: {
        logic: 'AND' | 'OR'
        rules: Array<{
          type: 'rule' | 'group'
          field?: string
          operator?: string
          value?: unknown
          logic?: 'AND' | 'OR'
          rules?: any[]
        }>
      }
    }>
  }
}

/**
 * Fetches default segmentation configuration from ISE backend
 * 
 * GET /segmentation-config/defaults
 * 
 * @param iseEndpoint - ISE backend endpoint
 * @returns Promise<ISegmentationConfigResponse | null>
 */
export const fetchDefaultSegmentationConfig = async (
  iseEndpoint: string,
): Promise<ISegmentationConfigResponse | null> => {
  const base = String(iseEndpoint).replace(/\/+$/g, '')
  const path = `segmentation-config/defaults`
  const url = `${base}/${path}`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      console.error(`Failed to fetch default segmentation config: ${response.status}`)
      return null
    }

    const json = await response.json()
    return json
  } catch (error) {
    console.error('Error fetching default segmentation config:', error)
    return null
  }
}

/**
 * Fetches segmentation configuration from ISE backend
 * 
 * GET /segmentation-config/{juid}
 * 
 * @param iseEndpoint - ISE backend endpoint
 * @param juid - Dealer account ID (jazelId)
 * @returns Promise<ISegmentationConfigResponse | null>
 */
export const fetchSegmentationConfig = async (
  iseEndpoint: string,
  juid: string,
  opts?: { includeDraft?: boolean },
): Promise<ISegmentationConfigResponse | null> => {
  const base = String(iseEndpoint).replace(/\/+$/g, '')
  const path = `segmentation-config/${encodeURIComponent(juid)}`
  const url = new URL(`${base}/${path}`)
  if (opts?.includeDraft) {
    url.searchParams.set('includeDraft', 'true')
  }

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      console.error(`Failed to fetch segmentation config: ${response.status}`)
      return null
    }

    const json = await response.json()
    return json
  } catch (error) {
    console.error('Error fetching segmentation config:', error)
    return null
  }
}

/**
 * Saves segmentation configuration to ISE backend
 * Always uses PUT (writes draft) regardless of onboarding status.
 * Onboarding completion happens via publish-drafts when user clicks "Finish".
 * 
 * @param iseEndpoint - ISE backend endpoint
 * @param juid - Dealer account ID (jazelId)
 * @param segments - Segments configuration object
 * @param isOnboarding - Ignored (kept for backward compatibility)
 * @param token - Optional session token for authentication
 * @returns Promise<boolean> - true if successful
 */
export const saveSegmentationConfig = async (
  iseEndpoint: string,
  juid: string,
  segments: Record<string, any>,
  isOnboarding: boolean = false,
  token?: string,
): Promise<boolean> => {
  const base = String(iseEndpoint).replace(/\/+$/g, '')
  
  // Always use PUT /segmentation-config/{juid} (writes draft)
  const url = `${base}/segmentation-config/${encodeURIComponent(juid)}`
  const method = 'PUT'
  const payload = {
    segments,
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers.authorization = `Bearer ${token}`
  }

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      console.error(`Failed to save segmentation config: ${response.status}`)
      return false
    }

    console.log('Segmentation config saved successfully')
    return true
  } catch (error) {
    console.error('Error saving segmentation config:', error)
    return false
  }
}

